// Shared bits for the Spin the Bottle screen + VR panels: names, the status
// line, the vote counts and "am I the world owner" (for removing players).

import { useEffect, useState } from 'react';
import { supabase } from '../../../config/supabase';
import { tableAction, type TableState } from '../gameStore';
import type { BottleState, BottleMove } from '../../../../supabase/functions/games/bottle.ts';

export type { BottleState };

export const nameOf = (st: TableState, g: BottleState | null, i: number | null | undefined) =>
  i === null || i === undefined ? '' : st.seats[i]?.name ?? g?.players[i]?.name ?? `Cushion ${i + 1}`;

export const playerCount = (st: TableState) => st.seats.filter(Boolean).length;
/** Votes needed for a majority. */
export const majorityOf = (n: number) => Math.floor(n / 2) + 1;

export const sendMove = (pid: string, move: BottleMove) => tableAction(pid, 'move', { move });

export const OPTION_LABELS = {
  mode: { tod: 'Truth or Dare', spin: 'Spin the Bottle' },
  spice: { mild: 'Mild', silly: 'Silly' },
} as const;
export const roundsLabel = (r: number) => (r === 0 ? 'Endless' : `${r} round${r === 1 ? '' : 's'}`);

/** One line for everyone: what's happening right now, from my point of view. */
export function statusLine(st: TableState, g: BottleState, seat: number): string {
  const who = (i: number | null) => (i === seat ? 'You' : nameOf(st, g, i));
  switch (g.phase) {
    case 'spin': return g.spinner === seat ? 'Your turn — spin the bottle!' : `${who(g.spinner)} is spinning the bottle…`;
    case 'spinning': return 'Round and round it goes…';
    case 'choose': {
      const t = g.target === seat ? 'you' : nameOf(st, g, g.target);
      if (g.chooser === seat) return g.mode === 'spin' && g.chooser !== g.target ? `Pick Truth or Dare for ${t}` : 'Truth or Dare? You pick!';
      return `It landed on ${t}! ${who(g.chooser)} ${g.chooser === seat ? 'pick' : 'picks'} Truth or Dare…`;
    }
    case 'card': return g.target === seat ? `Your ${g.card?.type === 'dare' ? 'dare' : 'truth'}:` : `${who(g.target)}'s ${g.card?.type === 'dare' ? 'dare' : 'truth'}:`;
    case 'cheer': return g.last ? `${g.last.seat === seat ? 'You' : g.last.name} did it! +${g.last.points}` : 'Nice!';
    case 'over': return 'Game over';
  }
}

/** The previous turn, in a few words. */
export function lastLine(g: BottleState): string | null {
  const l = g.last;
  if (!l || g.phase === 'cheer') return null;
  if (l.result === 'done') return `${l.name} did their ${l.type ?? 'card'} (+${l.points})`;
  if (l.result === 'pass') return `${l.name} passed`;
  if (l.result === 'timeout') return `${l.name} ran out of time`;
  return `${l.name} left the circle`;
}

/** Standings for display: seated players, best first. */
export function scoreRows(st: TableState, g: BottleState) {
  return st.seats
    .map((s, i) => (s ? { seat: i, name: s.name, score: g.players[i]?.score ?? 0, passes: g.players[i]?.passes ?? 0 } : null))
    .filter((r): r is { seat: number; name: string; score: number; passes: number } => !!r)
    .sort((a, b) => b.score - a.score || a.seat - b.seat);
}

export function winnerLine(st: TableState, g: BottleState, seat: number): string {
  const rows = scoreRows(st, g);
  if (!rows.length) return 'Game over';
  const top = rows[0].score;
  const tops = rows.filter((r) => r.score === top);
  const why = g.ended === 'vote' ? ' (ended by vote)' : g.ended === 'players' ? ' (not enough players)' : '';
  if (tops.length > 1) return `Tie at ${top} points: ${tops.map((r) => (r.seat === seat ? 'you' : r.name)).join(', ')}${why}`;
  return `${tops[0].seat === seat ? 'You win' : `${tops[0].name} wins`} with ${top} points!${why}`;
}

const ownerCache = new Map<string, Promise<string | null>>();

/** True if `me` owns the world (they may remove players from the game). The server checks too. */
export function useIsWorldOwner(worldId: string, me: string | null): boolean {
  const [owner, setOwner] = useState<string | null>(null);
  useEffect(() => {
    if (!worldId || !me) return;
    let p = ownerCache.get(worldId);
    if (!p) {
      p = Promise.resolve(supabase.from('worlds').select('owner_id').eq('id', worldId).maybeSingle())
        .then(({ data }) => (data as any)?.owner_id ?? null, () => null);
      ownerCache.set(worldId, p);
    }
    let live = true;
    void p.then((o) => { if (live) setOwner(o); });
    return () => { live = false; };
  }, [worldId, me]);
  return !!me && owner === me;
}
